import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mortalitiesService } from '../services/apiServices';
import { queryKeys } from '../config/queryClient';
import { Mortality, PaginatedResponse, MortalityStats } from '../types';

// Get all mortalities
export const useMortalities = (params?: any) => {
  return useQuery<PaginatedResponse<Mortality>>({
    queryKey: queryKeys.mortalitiesList(params),
    queryFn: () => mortalitiesService.getAll(params),
  });
};

// Get single mortality
export const useMortality = (id: number, enabled = true) => {
  return useQuery<Mortality>({
    queryKey: queryKeys.mortality(id),
    queryFn: () => mortalitiesService.getById(id),
    enabled,
  });
};

// Get mortalities stats
export const useMortalitiesStats = (params?: any) => {
  return useQuery<MortalityStats>({
    queryKey: queryKeys.mortalitiesStats(params),
    queryFn: () => mortalitiesService.getStats(params),
  });
};

// Get mortalities trends
export const useMortalitiesTrends = (params?: any) => {
  return useQuery({
    queryKey: queryKeys.mortalitiesTrends(params),
    queryFn: () => mortalitiesService.getTrends(params),
  });
};

// Create mortality
export const useCreateMortality = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Mortality>) => mortalitiesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mortalities'] });
      queryClient.invalidateQueries({ queryKey: ['mortalitiesStats'] });
      queryClient.invalidateQueries({ queryKey: ['mortalitiesTrends'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['birds'] });
    },
  });
};

// Update mortality
export const useUpdateMortality = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Mortality> }) =>
      mortalitiesService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['mortalities'] });
      queryClient.invalidateQueries({ queryKey: ['mortality', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['mortalitiesStats'] });
      queryClient.invalidateQueries({ queryKey: ['mortalitiesTrends'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['birds'] });
    },
  });
};

// Delete mortality
export const useDeleteMortality = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => mortalitiesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mortalities'] });
      queryClient.invalidateQueries({ queryKey: ['mortalitiesStats'] });
      queryClient.invalidateQueries({ queryKey: ['mortalitiesTrends'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['birds'] });
    },
  });
};
